/* eslint-disable jsx-a11y/alt-text */
import { Box, Grid, Typography } from '@material-ui/core';
import React from 'react';
import { simpleDateFormatter2 } from '../Shared/colorFormat';
import { parseHrFormNote } from '../common/hrFormNote';

const lineValue = (value: any) => {
  if (value === 0) return '0';
  if (!value) return '................................................';
  return `${value}`;
};

const firstNonEmpty = (...values: any[]) => {
  for (const value of values) {
    if (value === 0) return value;
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return value;
    }
  }
  return '';
};

const labelValue = ({ label, value }: any) => (
  <Box display="flex" mb={1}>
    <Typography style={{ fontWeight: 'bold', minWidth: 190 }}>
      {label}
    </Typography>
    <Typography>{lineValue(value)}</Typography>
  </Box>
);

const getEmployeeName = (employee: any, row: any, isRTL: boolean) => {
  if (isRTL) {
    return (
      employee?.nameAr ||
      employee?.name ||
      row?.employeeNameAr ||
      row?.employeeName ||
      ''
    );
  }
  return (
    employee?.name ||
    employee?.nameAr ||
    row?.employeeName ||
    row?.employeeNameAr ||
    ''
  );
};

export class EndServicePrint extends React.PureComponent<any, any> {
  renderHeader = (company: any) => (
    <Grid item xs={12}>
      {company?.header ? (
        <img
          src={company?.header}
          alt={company?.name}
          height="auto"
          width="100%"
          style={{ objectFit: 'contain', marginBottom: 10 }}
        />
      ) : (
        <Typography
          style={{ textAlign: 'center', fontWeight: 'bold' }}
          variant="h5"
        >
          {company?.name || ''}
        </Typography>
      )}
    </Grid>
  );

  renderSignatures = (isRTL: boolean) => (
    <Box mt={2} className="no-break">
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography>
            {isRTL ? 'اسم المسؤول:' : 'Responsible Name:'}
          </Typography>
          <Typography>
            ................................................
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{isRTL ? 'الصفة الوظيفية:' : 'Job Title:'}</Typography>
          <Typography>
            ................................................
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{isRTL ? 'التوقيع:' : 'Signature:'}</Typography>
          <Typography>
            ................................................
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography>{isRTL ? 'التاريخ:' : 'Date:'}</Typography>
          <Typography>........ / ........ / ........</Typography>
        </Grid>
      </Grid>
    </Box>
  );

  getDecisionLabel = ({ isRTL, title, reason }: any) => {
    const text = `${title || ''} ${reason || ''}`.toLowerCase();
    const isResignation =
      text.includes('استقال') ||
      text.includes('resign') ||
      text.includes('resignation');
    const isDismissal =
      text.includes('فصل') ||
      text.includes('dismiss') ||
      text.includes('termination');

    if (isRTL) {
      return `${isDismissal ? '☑' : '☐'} فصل من الوظيفة   ${isResignation ? '☑' : '☐'} استقالة   ${
        !isDismissal && !isResignation ? '☑' : '☐'
      } انهاء خدمة`;
    }

    return `${!isDismissal && !isResignation ? '☑' : '☐'} End Service   ${
      isResignation ? '☑' : '☐'
    } Resignation   ${isDismissal ? '☑' : '☐'} Dismissal`;
  };

  render() {
    const { company, isRTL, printData } = this.props;
    if (!printData) return <></>;

    const row = printData;
    const employee = printData?.employeeProfile || {};
    const meta = parseHrFormNote(row?.note) || {};
    const extra = meta?.extra || {};

    const employeeName = getEmployeeName(employee, row, isRTL);
    const jobTitle = firstNonEmpty(extra?.jobTitle, employee?.speciality);
    const nationality = firstNonEmpty(
      extra?.nationality,
      employee?.national,
      row?.national,
    );
    const identityNo = firstNonEmpty(
      employee?.nationalNo,
      employee?.pId,
      employee?.documentNo,
      employee?.workId,
      row?.nationalNo,
    );
    const startWorkDate = firstNonEmpty(
      employee?.startWorkDate,
      employee?.startDate,
      row?.startDate,
    );
    const terminationDate = firstNonEmpty(
      row?.time,
      row?.updatedAt,
      row?.createdAt,
    );
    const department = firstNonEmpty(
      isRTL ? employee?.departmentNameAr : employee?.departmentName,
      isRTL ? row?.departmentNameAr : row?.departmentName,
    );
    const reason = firstNonEmpty(meta?.notes, row?.desc);
    const phone = firstNonEmpty(employee?.phone, row?.employeePhone);

    return (
      <Box
        className="end-service-print"
        style={{ direction: isRTL ? 'rtl' : 'ltr', padding: 16, fontSize: 13 }}
      >
        <style>
          {`@media print {
            @page {
              size: A4 portrait;
              margin: 8mm;
            }
            .end-service-print {
              width: 100%;
              max-width: 210mm;
              margin: 0 auto;
            }
            .end-service-print .no-break {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          }`}
        </style>
        <Grid container spacing={0}>
          {this.renderHeader(company)}

          <Grid item xs={12}>
            <Typography
              style={{
                textAlign: 'center',
                fontWeight: 'bold',
                marginBottom: 16,
              }}
            >
              {isRTL
                ? 'نموذج انهاء خدمات موظف'
                : 'Employee End of Service Form'}
            </Typography>

            <Typography style={{ marginBottom: 12 }}>
              {isRTL ? 'اليوم:' : 'Date:'}{' '}
              {simpleDateFormatter2(terminationDate || new Date())}
            </Typography>

            {labelValue({
              label: isRTL ? 'اولا: بيانات الموظف' : 'Employee Information',
              value: '',
            })}
            {labelValue({
              label: isRTL ? 'الاسم' : 'Name',
              value: employeeName,
            })}
            {labelValue({
              label: isRTL ? 'المسمى الوظيفي' : 'Job Title',
              value: jobTitle,
            })}
            {labelValue({
              label: isRTL ? 'الجنسية' : 'Nationality',
              value: nationality,
            })}
            {labelValue({
              label: isRTL ? 'رقم الهوية / الإقامة' : 'ID / Residency No',
              value: identityNo,
            })}
            {labelValue({
              label: isRTL ? 'تاريخ المباشرة' : 'Start Work Date',
              value: simpleDateFormatter2(startWorkDate),
            })}
            {labelValue({
              label: isRTL ? 'تاريخ انهاء الخدمة' : 'End Service Date',
              value: simpleDateFormatter2(terminationDate || new Date()),
            })}
            {labelValue({
              label: isRTL ? 'رقم الجوال' : 'Phone',
              value: phone,
            })}
            {labelValue({
              label: isRTL ? 'القسم' : 'Department',
              value: department,
            })}
            {labelValue({
              label: isRTL ? 'سبب انهاء الخدمة' : 'Termination Reason',
              value: reason,
            })}
            {labelValue({
              label: isRTL ? 'حالة المخالصة' : 'Clearance Status',
              value: extra?.clearanceStatus,
            })}
            {labelValue({
              label: isRTL ? 'تسليم العهدة الى' : 'Handover To',
              value: extra?.handoverTo,
            })}
            {labelValue({
              label: isRTL ? 'ملاحظات العهدة' : 'Assets Notes',
              value: extra?.assetsNotes,
            })}
            {labelValue({
              label: isRTL ? 'ملاحظات الادارة' : 'Management Notes',
              value: extra?.managementNotes,
            })}

            <Box mt={2}>
              <Typography style={{ fontWeight: 'bold' }}>
                {isRTL ? 'ثانيا: إقرار الموظف' : 'Employee Declaration'}
              </Typography>
              <Typography style={{ lineHeight: 1.9 }}>
                {isRTL
                  ? 'اقر انا الموقع ادناه بانه تم استلام جميع ما بعهدتي (ان وجد)، ولا يوجد لي اي مطالبات مالية غير مسددة تجاه الشركة بعد تاريخ انهاء الخدمة المذكور.'
                  : 'I acknowledge that all assets in my custody (if any) have been handed over and I have no pending unpaid financial claims against the company after the end service date above.'}
              </Typography>
            </Box>

            <Box mt={2}>
              <Typography style={{ fontWeight: 'bold' }}>
                {isRTL ? 'ثالثا: قرار الشركة' : 'Company Decision'}
              </Typography>
              <Typography>
                {this.getDecisionLabel({
                  isRTL,
                  title: row?.title,
                  reason,
                })}
              </Typography>
            </Box>

            {this.renderSignatures(isRTL)}
          </Grid>
        </Grid>
      </Box>
    );
  }
}

export default EndServicePrint;
